import _ from "lodash";
import { http } from "../Guard";
import {
  RoadmapsData,
  RoadmapStagesData,
  RoadmapStage,
  RoadmapRequirementsData,
  RoadmapCompany,
  Questionnaire,
  QuestionnaireSectionsData,
  QuestionnaireQuestionsData,
  QuestionnaireQuestionChoicesData,
  QuestionnaireAnswer,
  QuestionnaireAnswersData,
  QuestionnaireAnswerInput,
  QuestionnaireAnswerCompleteInput,
  QuestionnaireQuestionAnswer,
  QuestionnaireQuestionAnswersData,
  QuestionnaireQuestionAnswerInput,
  QuestionnaireQuestionAnswerUpdateInput,
  MAX_PAGE_SIZE,
} from "./data.default";
import { QueryOperation } from "../code/search-query/search-query";
import {
  PaginationInput,
  CompanyPolicyData,
  CompanyRoleData,
  CompanyVesselData,
  CompanyRoleInput,
  CompanyVesselInput,
  CompanyVesselTypeData,
  CompanyData,
  CompanyUserData,
  EmployeeUserData,
  MyNotificationsData,
  MyNotificationRead,
  CompanyUser,
  UserHistoryData,
  DepartmentData,
  CompanyProfile,
  CompanyProfileInput,
  CompanyProfileInfo,
  OrganizationalMetricEntryData,
  OrganizationalMetricInput,
  CompanyFiscalPeriodData,
  CalendarEventsListData,
  CalendarEventInput,
  CalendarEventListItem,
  LoyaltyHistoryData,
  AssignQuestionnaireInput,
  Reward,
  RewardCreateInput,
  RewardsListData,
} from "./data.default";

// Users

export function getUserLoginHistory(
  input: PaginationInput,
): Promise<UserHistoryData> {
  const searchQuery = JSON.stringify(input.searchQuery);

  return http
    .get<any>("api/Data/UserLoginHistory", {
      params: {
        ..._.omit(input, "searchQuery"),
        searchQuery,
      },
    })
    .then(({ data }) => data);
}

export function getCompanyUsers(
  input: PaginationInput,
): Promise<CompanyUserData> {
  const searchQuery = JSON.stringify(input.searchQuery);

  return http
    .get("/api/Data/CompanyManagers", {
      params: {
        ..._.omit(input, "searchQuery"),
        searchQuery,
      },
    })
    .then(({ data }) => data);
}

export function getCompanyUser(uid: string): Promise<CompanyUser> {
  return http.get(`/api/Data/CompanyManagers/${uid}`).then(({ data }) => data);
}

export function deleteCompanyUser(uid: string) {
  return http
    .delete(`/api/Data/CompanyManagers/${uid}`)
    .then(({ data }) => data);
}

export function getCompanyUsersExport(input: PaginationInput) {
  const searchQuery = JSON.stringify(input.searchQuery);

  return http
    .get("/api/Data/CompanyManagers/Export", {
      responseType: "blob",
      params: {
        ..._.omit(input, "searchQuery"),
        searchQuery,
      },
    })
    .then(({ data }) => {
      try {
        const link = document.createElement("a");
        const urlBlob = window.URL.createObjectURL(new Blob([data]));
        try {
          link.href = urlBlob;
          link.setAttribute("download", "managers-export.xlsx");
          document.body.appendChild(link);
          link.click();
        } catch {
          throw new Error("Failed to download managers-export.xlsx.");
        } finally {
          link.remove();
          window.URL.revokeObjectURL(urlBlob);
        }
      } catch {
        throw new Error("Failed to download managers-export.xlsx.");
      }
    });
}

export function getCompanyEmployees(
  input: PaginationInput,
): Promise<EmployeeUserData> {
  const searchQuery = JSON.stringify(input.searchQuery);

  return http
    .get("/api/Data/CompanyEmployees", {
      params: {
        ..._.omit(input, "searchQuery"),
        searchQuery,
      },
    })
    .then(({ data }) => data);
}

// User Roles

export function getCompanyUserRoles(
  input: PaginationInput,
): Promise<CompanyRoleData> {
  const searchQuery = JSON.stringify(input.searchQuery);
  return http
    .get("/api/Data/CompanyRoles", {
      params: {
        ..._.omit(input, "searchQuery"),
        searchQuery,
      },
    })
    .then(({ data }) => data);
}

export function addCompanyUserRole(input: CompanyRoleInput) {
  return http.post("api/Data/CompanyUserRoles", input).then(({ data }) => data);
}

export function updateCompanyUserRole(input: CompanyRoleInput) {
  return http
    .patch(`/api/Data/CompanyUserRoles/${input.uid}`, _.omit(input, ["uid"]))
    .then(({ data }) => data);
}

export function deleteCompanyUserRole(uid: string) {
  return http
    .delete(`/api/Data/CompanyUserRoles/${uid}`)
    .then(({ data }) => data);
}

// Employee Roles

export function getCompanyEmployeeRoles(
  input: PaginationInput,
): Promise<CompanyRoleData> {
  const searchQuery = JSON.stringify(input.searchQuery);
  return http
    .get("/api/Data/CompanyEmployeeRoles", {
      params: {
        ..._.omit(input, "searchQuery"),
        searchQuery,
      },
    })
    .then(({ data }) => data);
}

export function addCompanyEmployeeRole(input: CompanyRoleInput) {
  return http
    .post("api/Data/CompanyEmployeeRoles", input)
    .then(({ data }) => data);
}

export function updateCompanyEmployeeRole(input: CompanyRoleInput) {
  return http
    .patch(
      `/api/Data/CompanyEmployeeRoles/${input.uid}`,
      _.omit(input, ["uid"]),
    )
    .then(({ data }) => data);
}

export function deleteCompanyEmployeeRole(uid: string) {
  return http
    .delete(`/api/Data/CompanyEmployeeRoles/${uid}`)
    .then(({ data }) => data);
}

// Policies

export function getCompanyPolicies(
  input: PaginationInput,
): Promise<CompanyPolicyData> {
  const searchQuery = JSON.stringify(input.searchQuery);
  return http
    .get("api/Data/CompanyPolicies", {
      params: {
        ..._.omit(input, "searchQuery"),
        searchQuery,
      },
    })
    .then(({ data }) => data);
}

// PD-02 §6.7 — company-scoped wellbeing reports (operator publishes aggregated
// reports back to the company; this count feeds Psychosocial Risk card).
export function getWellbeingReports(input: PaginationInput): Promise<any> {
  const searchQuery = JSON.stringify(input.searchQuery);
  return http
    .get("/api/Data/WellbeingReports", {
      params: { ..._.omit(input, "searchQuery"), searchQuery },
    })
    .then(({ data }) => data);
}

// PD-02 §6.16.5 — assign a reward to one employee. Backend permission is
// AppCompanyUser:Create on RewardsAssignment; RequireUserTargetAsync scopes
// the target to the caller's own company so this cannot leak across tenants.
export function assignRewardToUser(input: {
  readonly rewardUid: string;
  readonly userUid: string;
  readonly assignmentDate?: string | null;
  readonly expirationDate?: string | null;
}): Promise<void> {
  const { rewardUid, userUid, ...body } = input;
  return http
    .post(`/api/Data/RewardsAssignment/${rewardUid}/User/${userUid}`, body)
    .then(() => undefined);
}

export function assignRewardToDepartment(input: {
  readonly rewardUid: string;
  readonly departmentUid: string;
  readonly assignmentDate?: string | null;
  readonly expirationDate?: string | null;
}): Promise<void> {
  const { rewardUid, departmentUid, ...body } = input;
  return http
    .post(`/api/Data/RewardsAssignment/${rewardUid}/Department/${departmentUid}`, body)
    .then(() => undefined);
}

// PD-02 §6.16.8 — company managers monitor incidents reported in their
// company. Backend auto-scopes to the caller's company via
// CompanyAuthorizedGenericCrudeApiController.
export function getIncidents(input: PaginationInput): Promise<any> {
  const searchQuery = JSON.stringify(input.searchQuery);
  return http
    .get("/api/Data/Incidents", {
      params: { ..._.omit(input, "searchQuery"), searchQuery },
    })
    .then(({ data }) => data);
}

// Well-Being Corner (PD-02 §7.2) — list of materials available to the
// company (platform-provided + company-scoped). Backend scopes by caller.
export function getWellbeingMaterials(
  input: PaginationInput,
): Promise<any> {
  const searchQuery = JSON.stringify(input.searchQuery);
  return http
    .get("/api/Data/WellbeingMaterials", {
      params: { ..._.omit(input, "searchQuery"), searchQuery },
    })
    .then(({ data }) => data);
}

export function getTrainings(
  input: PaginationInput,
): Promise<any> {
  const searchQuery = JSON.stringify(input.searchQuery);
  return http
    .get("/api/Data/Trainings", {
      params: { ..._.omit(input, "searchQuery"), searchQuery },
    })
    .then(({ data }) => data);
}

export function previewCompanyPolicy(uid: string) {
  return http
    .get(`/api/Data/CompanyPolicies/${uid}/Pdf`, {
      responseType: "blob",
    })
    .then(({ data }) => {
      const pdfUrl = URL.createObjectURL(data);

      window.open(pdfUrl, "_blank");
    });
}

// PD-02 §6.15 — publish a new policy. Backend expects multipart form (Name /
// Code / Description / PdfFile), scoped to the caller's company server-side.
export function addCompanyPolicy(input: {
  readonly name: string;
  readonly code: string;
  readonly description?: string;
  readonly pdfFile: File;
}): Promise<void> {
  const form = new FormData();
  form.append("name", input.name);
  form.append("code", input.code);
  if (input.description) form.append("description", input.description);
  form.append("pdfFile", input.pdfFile);
  return http
    .post("/api/Data/CompanyPolicies", form, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then(() => undefined);
}

// Fire a reminder to employees who haven't acknowledged a policy yet. Returns
// the queued count. Narrow by department / userUids when needed.
export function sendPolicyReminder(
  policyUid: string,
  input?: { readonly departmentUid?: string; readonly userUids?: string[] },
): Promise<number> {
  return http
    .post<number>(`/api/Data/CompanyPolicies/${policyUid}/SendReminder`, input ?? {})
    .then(({ data }) => data);
}

// Policy acknowledgments monitoring (reused on the company side so the
// publishing manager can see who still owes an ack).
export function getCompanyPolicyAcknowledgments(
  input: PaginationInput,
): Promise<any> {
  const searchQuery = JSON.stringify(input.searchQuery);
  return http
    .get("api/Data/CompanyPolicyAcknowledgments", {
      params: { ..._.omit(input, "searchQuery"), searchQuery },
    })
    .then(({ data }) => data);
}

// Vessels

export function getCompanyVessels(
  input: PaginationInput,
): Promise<CompanyVesselData> {
  const searchQuery = JSON.stringify(input.searchQuery);

  return http
    .get("/api/Data/CompanyVessels", {
      params: {
        ..._.omit(input, "searchQuery"),
        searchQuery,
      },
    })
    .then(({ data }) => data);
}

export function addCompanyVessel(input: CompanyVesselInput) {
  return http.post("/api/Data/CompanyVessels", input).then(({ data }) => data);
}

export function updateCompanyVessel(input: CompanyVesselInput) {
  return http
    .patch(`/api/Data/CompanyVessels/${input.uid}`, _.omit(input, ["uid"]))
    .then(({ data }) => data);
}

export function deleteCompanyVessel(uid: string) {
  return http
    .delete(`/api/Data/CompanyVessels/${uid}`)
    .then(({ data }) => data);
}

export function getCompanyVesselTypes(
  input: PaginationInput,
): Promise<CompanyVesselTypeData> {
  const searchQuery = JSON.stringify(input.searchQuery);

  return http
    .get("/api/Data/CompanyVesselTypes", {
      params: {
        ..._.omit(input, "searchQuery"),
        searchQuery,
      },
    })
    .then(({ data }) => data);
}

// Departments

export function getCompanyDepartments(
  input: PaginationInput,
): Promise<DepartmentData> {
  const searchQuery = JSON.stringify(input.searchQuery);

  return http
    .get("/api/Data/CompanyDepartments", {
      params: {
        ..._.omit(input, "searchQuery"),
        searchQuery,
      },
    })
    .then(({ data }) => data);
}

export function addCompanyDepartment(input: CompanyRoleInput) {
  return http
    .post("api/Data/CompanyDepartments", input)
    .then(({ data }) => data);
}

export function updateCompanyDepartment(input: CompanyRoleInput) {
  return http
    .patch(`/api/Data/CompanyDepartments/${input.uid}`, _.omit(input, ["uid"]))
    .then(({ data }) => data);
}

export function deleteCompanyDepartment(uid: string) {
  return http
    .delete(`/api/Data/CompanyDepartments/${uid}`)
    .then(({ data }) => data);
}

// Organizational metrics (manager-scoped server-side; no company filter needed)

export function getOrganizationalMetrics(
  input: PaginationInput,
): Promise<OrganizationalMetricEntryData> {
  const searchQuery = JSON.stringify(input.searchQuery);

  return http
    .get("/api/Data/OrganizationalMetrics", {
      params: {
        ..._.omit(input, "searchQuery"),
        searchQuery,
      },
    })
    .then(({ data }) => data);
}

export function addOrganizationalMetric(input: OrganizationalMetricInput) {
  return http
    .post("api/Data/OrganizationalMetrics", input)
    .then(({ data }) => data);
}

export function updateOrganizationalMetric(input: OrganizationalMetricInput) {
  return http
    .patch(`/api/Data/OrganizationalMetrics/${input.uid}`, _.omit(input, ["uid"]))
    .then(({ data }) => data);
}

// Fiscal periods (read; manager-scoped server-side)

export function getCompanyFiscalPeriods(
  input: PaginationInput,
): Promise<CompanyFiscalPeriodData> {
  const searchQuery = JSON.stringify(input.searchQuery);

  return http
    .get("/api/Data/CompanyFiscalPeriods", {
      params: {
        ..._.omit(input, "searchQuery"),
        searchQuery,
      },
    })
    .then(({ data }) => data);
}

// Companies

export function getCompanies(input: PaginationInput): Promise<CompanyData> {
  const searchQuery = JSON.stringify(input.searchQuery);
  return http
    .get("api/Data/Companies", {
      params: {
        ..._.omit(input, "searchQuery"),
        searchQuery,
      },
    })
    .then(({ data }) => data);
}

// Notifications

export function getMyNotifications(
  input: PaginationInput,
): Promise<MyNotificationsData> {
  const searchQuery = JSON.stringify(input.searchQuery);

  return http
    .get("/api/Data/MyNotifications", {
      params: {
        ..._.omit(input, "searchQuery"),
        searchQuery,
      },
    })
    .then(({ data }) => data);
}

export function readMyNotification(input: MyNotificationRead): Promise<any> {
  return http
    .patch(`/api/Data/MyNotifications/${input.uid}/read`)
    .then(({ data }) => data);
}

export function acknowledgeDelivery(input: MyNotificationRead): Promise<void> {
  return http
    .patch(`/api/Data/MyNotifications/${input.uid}/delivered`)
    .then(({ data }) => data);
}

// Company Profile

export function getCompanyProfile(uid: string): Promise<CompanyProfileInfo> {
  return http
    .get(`/api/Data/Accounts/Companies/${uid}`)
    .then(({ data }) => data);
}

export function updateCompany(
  input: CompanyProfileInput,
): Promise<CompanyProfile> {
  return http
    .patch(`/api/Data/Companies/${input.uid}`, _.omit(input, ["uid"]))
    .then(({ data }) => data);
}

// ── Processes (Roadmaps) — read-only journey content + self-scoped progress ──────────────────

export function getRoadmaps(input: PaginationInput): Promise<RoadmapsData> {
  const searchQuery = JSON.stringify(input.searchQuery);
  return http
    .get("/api/Data/Roadmaps", { params: { ..._.omit(input, "searchQuery"), searchQuery } })
    .then(({ data }) => data);
}

export function getRoadmapStages(input: PaginationInput): Promise<RoadmapStagesData> {
  const searchQuery = JSON.stringify(input.searchQuery);
  return http
    .get("/api/Data/RoadmapsStages", { params: { ..._.omit(input, "searchQuery"), searchQuery } })
    .then(({ data }) => data);
}

export function getRoadmapStage(uid: string): Promise<RoadmapStage> {
  return http.get(`/api/Data/RoadmapsStages/${uid}`).then(({ data }) => data);
}

export function getRoadmapRequirements(
  input: PaginationInput,
): Promise<RoadmapRequirementsData> {
  const searchQuery = JSON.stringify(input.searchQuery);
  return http
    .get("/api/Data/RoadmapsStageRequirements", {
      params: { ..._.omit(input, "searchQuery"), searchQuery },
    })
    .then(({ data }) => data);
}

// Self-scoped: the caller's own company progress (204 → not started → null).
export function getMyRoadmapProgress(
  roadmapUid: string,
): Promise<RoadmapCompany | null> {
  return http
    .get(`/api/Data/MyRoadmapProgress/${roadmapUid}`)
    .then(({ data, status }) => (status === 204 || !data ? null : data));
}

export function startMyRoadmapProgress(roadmapUid: string): Promise<RoadmapCompany> {
  return http
    .post(`/api/Data/MyRoadmapProgress/${roadmapUid}/Start`)
    .then(({ data }) => data);
}

// NOTE: managers never advance their own journey — progression is operator-driven (an admin
// reviews a completed requirement and advances the company). There is no self-advance call.

// ── Questionnaires + answers (manager answers on the spot, draft-first) ───────────────────────

export function getQuestionnaire(uid: string): Promise<Questionnaire> {
  return http.get(`/api/Data/Questionnaires/${uid}`).then(({ data }) => data);
}

export function getQuestionnaireSections(
  questionnaireUid: string,
): Promise<QuestionnaireSectionsData> {
  return http
    .get("/api/Data/QuestionnairesSections", {
      params: {
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
        searchQuery: JSON.stringify({
          conditions: [
            { field: "questionnaireUid", values: [questionnaireUid], operation: QueryOperation.Equal },
          ],
        }),
        sortBy: "order",
        sortDescending: false,
      },
    })
    .then(({ data }) => data);
}

export function getSectionQuestions(
  sectionUid: string,
): Promise<QuestionnaireQuestionsData> {
  return http
    .get("/api/Data/QuestionnairesQuestions", {
      params: {
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
        searchQuery: JSON.stringify({
          conditions: [
            { field: "sectionUid", values: [sectionUid], operation: QueryOperation.Equal },
          ],
        }),
        sortBy: "order",
        sortDescending: false,
      },
    })
    .then(({ data }) => data);
}

export function getQuestionChoices(
  questionUid: string,
): Promise<QuestionnaireQuestionChoicesData> {
  return http
    .get("/api/Data/QuestionnairesQuestionsChoices", {
      params: {
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
        searchQuery: JSON.stringify({
          conditions: [
            { field: "questionUid", values: [questionUid], operation: QueryOperation.Equal },
          ],
        }),
        sortBy: "order",
        sortDescending: false,
      },
    })
    .then(({ data }) => data);
}

// All of the caller's questionnaire answers (to derive Start/Edit/View + last-updated per step).
export function getMyQuestionnaireAnswersForUser(
  userUid: string,
): Promise<QuestionnaireAnswersData> {
  return http
    .get("/api/Data/QuestionnairesAnswers", {
      params: {
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
        searchQuery: JSON.stringify({
          conditions: [{ field: "userUid", values: [userUid], operation: QueryOperation.Equal }],
        }),
      },
    })
    .then(({ data }) => data);
}

// One batch read of all question-answers across the given answer headers (SQL IN) — used to compute
// the true "last updated" per step client-side without N+1 queries.
export function getQuestionAnswersForAnswers(
  answerUids: string[],
): Promise<QuestionnaireQuestionAnswersData> {
  return http
    .get("/api/Data/QuestionnairesQuestionAnswers", {
      params: {
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
        searchQuery: JSON.stringify({
          conditions: [
            { field: "questionnaireAnswerUid", values: answerUids, operation: QueryOperation.In },
          ],
        }),
      },
    })
    .then(({ data }) => data);
}

// Find the caller's existing answer for a questionnaire (draft to resume), scoped to own userUid.
export function getMyQuestionnaireAnswers(
  userUid: string,
  questionnaireUid: string,
): Promise<QuestionnaireAnswersData> {
  return http
    .get("/api/Data/QuestionnairesAnswers", {
      params: {
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
        searchQuery: JSON.stringify({
          conditions: [
            { field: "userUid", values: [userUid], operation: QueryOperation.Equal },
            { field: "questionnaireUid", values: [questionnaireUid], operation: QueryOperation.Equal },
          ],
        }),
      },
    })
    .then(({ data }) => data);
}

export function getQuestionnaireQuestionAnswers(
  questionnaireAnswerUid: string,
): Promise<QuestionnaireQuestionAnswersData> {
  return http
    .get("/api/Data/QuestionnairesQuestionAnswers", {
      params: {
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
        searchQuery: JSON.stringify({
          conditions: [
            { field: "questionnaireAnswerUid", values: [questionnaireAnswerUid], operation: QueryOperation.Equal },
          ],
        }),
      },
    })
    .then(({ data }) => data);
}

export function createQuestionnaireAnswer(
  input: QuestionnaireAnswerInput,
): Promise<QuestionnaireAnswer> {
  return http.post("/api/Data/QuestionnairesAnswers", input).then(({ data }) => data);
}

export function completeQuestionnaireAnswer(
  input: QuestionnaireAnswerCompleteInput,
): Promise<any> {
  return http
    .patch(`/api/Data/QuestionnairesAnswers/${input.uid}`, _.omit(input, ["uid"]))
    .then(({ data }) => data);
}

export function addQuestionnaireQuestionAnswer(
  input: QuestionnaireQuestionAnswerInput,
): Promise<QuestionnaireQuestionAnswer> {
  return http
    .post("/api/Data/QuestionnairesQuestionAnswers", input)
    .then(({ data }) => data);
}

export function updateQuestionnaireQuestionAnswer(
  input: QuestionnaireQuestionAnswerUpdateInput,
): Promise<any> {
  return http
    .patch(`/api/Data/QuestionnairesQuestionAnswers/${input.uid}`, _.omit(input, ["uid"]))
    .then(({ data }) => data);
}

// ── Calendar events (company creates events for employees) ─────────────────

export function getCalendarEventsList(
  input: PaginationInput,
): Promise<CalendarEventsListData> {
  const searchQuery = JSON.stringify(input.searchQuery);
  return http
    .get("/api/Data/CalendarEvents", {
      params: { ..._.omit(input, "searchQuery"), searchQuery },
    })
    .then(({ data }) => data);
}

export function addCalendarEvent(
  input: CalendarEventInput,
): Promise<CalendarEventListItem> {
  return http
    .post("/api/Data/CalendarEvents", input)
    .then(({ data }) => data);
}

// ── Loyalty history (scoped by company — leaderboard aggregation) ──────────

export function getLoyaltyHistory(
  input: PaginationInput,
): Promise<LoyaltyHistoryData> {
  const searchQuery = JSON.stringify(input.searchQuery);
  return http
    .get("/api/Data/LoyaltyHistory", {
      params: { ..._.omit(input, "searchQuery"), searchQuery },
    })
    .then(({ data }) => data);
}

// ── Assign assessment (OCEAN IV etc.) ──────────────────────────────────────

export function assignQuestionnaire(
  input: AssignQuestionnaireInput,
): Promise<any> {
  return http
    .post("/api/Data/QuestionnairesAnswers", input)
    .then(({ data }) => data);
}

// ── Rewards (Company can CRUD its own rewards, assignment stays admin-only) ─

export function getRewards(input: PaginationInput): Promise<RewardsListData> {
  const searchQuery = JSON.stringify(input.searchQuery);
  return http
    .get("/api/Data/Rewards", {
      params: { ..._.omit(input, "searchQuery"), searchQuery },
    })
    .then(({ data }) => data);
}

export function addReward(input: RewardCreateInput): Promise<Reward> {
  return http
    .post("/api/Data/Rewards", input)
    .then(({ data }) => data);
}
